import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { marketing } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { Palette, Share2, Eye, Users, Loader2, Save, ExternalLink, Copy } from 'lucide-react';
import ImagePicker from '../components/ImagePicker';

// Owner-facing editor for the auto-generated Tier-3 landing page. Owners on
// Tier 1/2 land on the locked upgrade screen instead (route gate).
export default function LandingPageBuilder() {
  const [pages, setPages] = useState(null);
  const [locked, setLocked] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();

  const load = async () => {
    try {
      const r = await marketing.landing();
      setPages(r);
      if (r?.length && !activeId) setActiveId(r[0].id);
    } catch (e) {
      if (e?.response?.status === 403) setLocked(true);
      else toast({ title: 'Failed to load landing pages' });
      setPages([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (locked) return <MarketingLocked />;
  if (pages === null) return <div className="text-gray-400 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>;
  if (pages.length === 0) return (
    <div className="pbb-card p-8 text-center" data-testid="landing-empty">
      <div className="font-semibold text-white">No Full-Package business yet</div>
      <p className="text-sm text-gray-400 mt-1">Upgrade one of your businesses to Full Package to auto-generate a landing page.</p>
      <Link to="/upgrade" className="pbb-btn-green mt-4 inline-flex max-w-xs">Upgrade a listing</Link>
    </div>
  );

  const active = pages.find(p => p.id === activeId) || pages[0];
  const setF = (k, v) => setPages(prev => prev.map(p => p.id === active.id ? { ...p, [k]: v } : p));

  const save = async () => {
    setSaving(true);
    try {
      const patch = { headline: active.headline, subheadline: active.subheadline,
        body: active.body, heroImage: active.heroImage, logo: active.logo, colors: active.colors,
        ctaText: active.ctaText, ctaUrl: active.ctaUrl, showOptIn: active.showOptIn,
        optInLabel: active.optInLabel };
      const fresh = await marketing.updateLanding(active.id, patch);
      setPages(prev => prev.map(p => p.id === fresh.id ? fresh : p));
      toast({ title: 'Landing page saved' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Try again' });
    } finally { setSaving(false); }
  };

  const shareUrl = `${window.location.origin}/l/${active.slug}`;
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast({ title: 'Link copied' }); }
    catch { toast({ title: 'Copy failed', description: shareUrl }); }
  };

  return (
    <div className="space-y-4" data-testid="landing-page-builder">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold inline-flex items-center gap-2"><Palette size={22} className="text-yellow-400"/> Landing Page Builder</h1>
          <p className="text-gray-400 text-sm">Auto-built from your profile — edit anything, then share the link everywhere.</p>
        </div>
        <div className="flex items-center gap-2">
          <a href={shareUrl} target="_blank" rel="noreferrer" data-testid="landing-view-live"
            className="text-xs px-3 py-1.5 rounded-lg border border-[#2a2a2a] hover:border-green-500 text-gray-200 inline-flex items-center gap-1">
            <Eye size={12}/> View live
          </a>
          <button onClick={copyLink} data-testid="landing-copy-link"
            className="text-xs px-3 py-1.5 rounded-lg border border-yellow-500/40 text-yellow-200 hover:bg-yellow-500/10 inline-flex items-center gap-1">
            <Copy size={12}/> Copy share link
          </button>
        </div>
      </div>

      {pages.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {pages.map(p => (
            <button key={p.id} onClick={() => setActiveId(p.id)}
              data-testid={`landing-tab-${p.slug}`}
              className={`text-xs px-3 py-1.5 rounded-lg border ${p.id === active.id ? 'border-green-500 bg-green-500/10 text-white' : 'border-[#2a2a2a] text-gray-300 hover:border-gray-500'}`}>
              /l/{p.slug}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-3">
          <Field label="Headline">
            <input value={active.headline || ''} onChange={e => setF('headline', e.target.value)}
              data-testid="landing-headline" maxLength={140} className="pbb-input"/>
          </Field>
          <Field label="Subheadline">
            <input value={active.subheadline || ''} onChange={e => setF('subheadline', e.target.value)}
              data-testid="landing-subheadline" maxLength={200} className="pbb-input"/>
          </Field>
          <Field label="Body / description">
            <textarea rows={5} value={active.body || ''} onChange={e => setF('body', e.target.value)}
              data-testid="landing-body" className="pbb-input"/>
          </Field>
          <Field label="Hero image">
            <ImagePicker value={active.heroImage} onChange={v => setF('heroImage', v)}
              kind="post" testid="landing-hero-image"/>
          </Field>
          <Field label="Logo (small)">
            <ImagePicker value={active.logo} onChange={v => setF('logo', v)}
              kind="post" testid="landing-logo"/>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="CTA button text">
              <input value={active.ctaText || ''} onChange={e => setF('ctaText', e.target.value)}
                data-testid="landing-cta-text" maxLength={40} className="pbb-input"/>
            </Field>
            <Field label="CTA URL">
              <input value={active.ctaUrl || ''} onChange={e => setF('ctaUrl', e.target.value)}
                data-testid="landing-cta-url" placeholder="https://…" className="pbb-input"/>
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Primary color">
              <input type="color" value={active.colors?.primary || '#16a34a'}
                onChange={e => setF('colors', { ...(active.colors || {}), primary: e.target.value })}
                data-testid="landing-color-primary" className="w-full h-9 rounded border border-[#2a2a2a] bg-black"/>
            </Field>
            <Field label="Accent color">
              <input type="color" value={active.colors?.accent || '#fbbf24'}
                onChange={e => setF('colors', { ...(active.colors || {}), accent: e.target.value })}
                data-testid="landing-color-accent" className="w-full h-9 rounded border border-[#2a2a2a] bg-black"/>
            </Field>
            <Field label="Background">
              <input type="color" value={active.colors?.bg || '#ffffff'}
                onChange={e => setF('colors', { ...(active.colors || {}), bg: e.target.value })}
                data-testid="landing-color-bg" className="w-full h-9 rounded border border-[#2a2a2a] bg-black"/>
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-200 mt-1">
            <input type="checkbox" checked={!!active.showOptIn}
              onChange={e => setF('showOptIn', e.target.checked)}
              data-testid="landing-show-optin" className="accent-green-500"/>
            Show &ldquo;opt-in for deals&rdquo; checkbox to visitors
          </label>
          {active.showOptIn && (
            <Field label="Opt-in checkbox label">
              <input value={active.optInLabel || ''} onChange={e => setF('optInLabel', e.target.value)}
                data-testid="landing-optin-label" maxLength={120} className="pbb-input"/>
            </Field>
          )}
          <button onClick={save} disabled={saving} data-testid="landing-save"
            className="pbb-btn-green inline-flex items-center gap-2 mt-1">
            {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Save changes
          </button>
        </div>

        {/* Live preview */}
        <div className="lg:sticky lg:top-4 self-start">
          <div className="text-[11px] tracking-widest text-gray-400 uppercase mb-2">Live preview</div>
          <LandingPreview page={active} />
          <div className="mt-3 flex items-center gap-3 text-xs text-gray-400">
            <span className="inline-flex items-center gap-1"><Eye size={11}/> {active.viewCount || 0} views</span>
            <span className="inline-flex items-center gap-1"><Users size={11}/> {active.optInCount || 0} opt-ins</span>
            <span className="inline-flex items-center gap-1"><Share2 size={11}/> {active.shareCount || 0} shares</span>
            <Link to="/marketing/contacts" data-testid="landing-view-contacts"
              className="ml-auto text-green-400 hover:underline">View contacts →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] text-gray-400 mb-1 font-semibold uppercase tracking-wider">{label}</div>
      {children}
    </label>
  );
}

export function LandingPreview({ page }) {
  const c = page.colors || {};
  return (
    <div className="rounded-2xl overflow-hidden border border-[#2a2a2a]" data-testid="landing-preview"
      style={{ background: c.bg || '#ffffff', color: c.text || '#111111' }}>
      {page.heroImage && (
        <div className="w-full h-40 bg-black">
          <img src={page.heroImage} alt="" className="w-full h-full object-cover"/>
        </div>
      )}
      <div className="p-6">
        {page.logo && <img src={page.logo} alt="" className="h-10 mb-3 object-contain"/>}
        <h2 className="text-2xl font-bold" style={{ color: c.primary || '#16a34a' }}>{page.headline || 'Headline'}</h2>
        <div className="text-sm opacity-80 mt-1">{page.subheadline}</div>
        <p className="text-sm mt-3 whitespace-pre-wrap opacity-90">{page.body}</p>
        {page.ctaText && (
          <div className="mt-4">
            <span className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-white text-sm font-semibold"
              style={{ background: c.primary || '#16a34a' }}>
              {page.ctaText} <ExternalLink size={12}/>
            </span>
          </div>
        )}
        {page.showOptIn && (
          <div className="mt-4 text-xs opacity-80">
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" disabled className="accent-green-600"/>
              {page.optInLabel || 'Send me deals'}
            </label>
          </div>
        )}
      </div>
    </div>
  );
}

// Locked screen for T1/T2 owners.
export function MarketingLocked() {
  return (
    <div className="pbb-card p-10 text-center max-w-lg mx-auto" data-testid="marketing-locked">
      <div className="text-4xl mb-2">🔒</div>
      <h2 className="text-xl font-bold text-white">Marketing Suite is a Full Package feature</h2>
      <p className="text-sm text-gray-400 mt-2">Upgrade to the $120/month Full Package to unlock landing pages, contact lists, campaigns, auto-responders, and the homepage banner slider.</p>
      <Link to="/upgrade" data-testid="marketing-locked-upgrade" className="pbb-btn-green mt-5 inline-flex max-w-xs">Upgrade a listing</Link>
    </div>
  );
}
